import React, { useState } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import Select, { components } from 'react-select';
import MaskedInput from 'react-text-mask';
import { getInvalidFieldClass } from '../../../helpers/validation';
import partnerSchemas from '../../../helpers/validation/schemas/PartnerFields';
import catalogSchemas from '../../../helpers/validation/schemas/CatalogFields';
import FormType from './PropTypes/PVPartnerContentForm';
import { createHTMLText } from '../../../helpers/textFunctions';
import USAStates from '../../../helpers/json/USAStates';
import { WebServiceHosts, AuthHeader } from '../../../helpers/siteConfigConstants';

const PVTemplate1FormCatalog = (props) => {
    const { formData } = props;

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formSuccess, setFormSuccess] = useState(false);
    const [formError, setFormError] = useState(false);

    const defaultEmailAddress = 'SEORecruitingpages@perfect-vision.com';

    const initialValues = {
        FullName: '',
        Company: '',
        Phone: '',
        Email: '',
        Address1: '',
        Address2: '',
        City: '',
        State: '',
        ZipCode: '',
    };

    const validationSchema = Yup.object().shape({
        FullName: partnerSchemas.fullName,
        Company: partnerSchemas.companyRequired,
        Phone: partnerSchemas.phoneRequired,
        Email: partnerSchemas.emailRequired,
        Address1: catalogSchemas.addressRequired,
        Address2: catalogSchemas.address,
        City: catalogSchemas.cityRequired,
        State: catalogSchemas.stateUSAbbrevRequired,
        ZipCode: partnerSchemas.usZip5Optional4Required,
    });

    const getButtonStyle = () =>
        formData.class.toLowerCase() === 'pv-template1-form-catalog'
            ? 'btn-default'
            : 'btn-primary';

    const pvSelectStyles = {
        container: (provided, state) => ({
            ...provided,
            backgroundColor: 'hsl(0,0%,100%)',
            border: state.isFocused ? '1px solid #0033A0' : '1px solid #bdbdbd',
        }),
        option: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#FFFFFF' : '#333333',
            backgroundColor: state.isFocused ? '#0033A0' : '#FFFFFF',
            padding: 5,
        }),
        control: () => ({
            alignItems: 'center',
            borderRadius: 0,
            cursor: 'default',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            minHeight: '30px',
            outline: '0 !important',
            position: 'relative',
            transition: 'all 100ms',
            boxSizing: 'border-box',
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            visibility: 'hidden',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '0',
            margin: '0',
        }),
    };

    const DropdownIndicator = (innerProps) => {
        return (
            components.DropdownIndicator && (
                <components.DropdownIndicator {...innerProps}>
                    <i className="fa fa-sort" />
                </components.DropdownIndicator>
            )
        );
    };

    const statesList = USAStates.concat([{ Name: '', Abbr: '' }]);

    const handleSubmit = (values, actions) => {
        actions.validateForm();
        setFormSubmitting(true);
        const dealerRequestUri = `${WebServiceHosts.WebStore}/api/Resources/CatalogRequest`;
        const v = values;
        const emailTo = () => {
            const emailObj = formData.emailTo.find((obj) => obj.type.toLowerCase() === 'default');
            if (emailObj) {
                return emailObj.emailAddress;
            }
            return defaultEmailAddress;
        };
        const thisURL = `${window.location.protocol}//${window.location.host}/${window.location.pathname}`;
        const fetchBody = JSON.stringify({
            FullName: v.FullName,
            Company: v.Company,
            Phone: v.Phone,
            Email: v.Email,
            Address1: v.Address1,
            Address2: v.Address2,
            City: v.City,
            State: v.State,
            ZipCode: v.ZipCode,
            EmailTo: emailTo(),
            PageLink: thisURL,
            ReferringURL: document.referrer,
        });
        fetch(dealerRequestUri, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
                Authorization: AuthHeader,
                'Content-Type': 'application/json',
            },
            body: fetchBody,
        })
            .then((res) => res.json())
            .then((response) => {
                setFormSubmitted(true);
                if (response === 'success') {
                    setFormSuccess(true);
                } else {
                    setFormError(true);
                }
            })
            .catch((_error) => {
                setFormSubmitted(true);
                setFormError(true);
            });
        /* eslint-enable no-console */
    };

    const zipMask = (rawValue) => {
        if (rawValue.length < 6) {
            return [/\d/, /\d/, /\d/, /\d/, /\d/];
        }
        return [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
    };

    const PVFormikTextBox = (tbProps) => {
        const { id, type, label, mask, maskGuide, formObj, fieldObj } = tbProps;
        const thisType = !type ? 'text' : type;
        const thisMask = !mask ? false : mask;
        const thisMaskGuide = !maskGuide ? false : maskGuide;
        return (
            <div className="pv form-group">
                <label htmlFor={id} className="control-label">
                    {label}
                </label>
                <MaskedInput
                    {...fieldObj}
                    type={thisType}
                    className={`pv form-control ${getInvalidFieldClass(fieldObj.name, formObj)}`}
                    id={id}
                    mask={thisMask}
                    guide={thisMaskGuide}
                />
                <div
                    className={`pv-text-tiny ${
                        getInvalidFieldClass(fieldObj.name, formObj) === '' ? 'hidden' : ''
                    }`}
                >
                    {formObj.errors[fieldObj.name]}
                </div>
            </div>
        );
    };

    /* eslint-disable react/no-danger */
    return (
        <React.Fragment>
            <div className="pv-template1-header-form-wrapper">
                <div className={`pv-template1-header-form-box ${formData.class}`}>
                    <div className={formSubmitted ? 'hidden' : ''}>
                        <div
                            className="pv-template1-header-form-header"
                            dangerouslySetInnerHTML={createHTMLText(formData.title)}
                        />
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={(values, actions) => handleSubmit(values, actions)}
                        >
                            <Form>
                                <div className="pv form-group">
                                    <Field name="FullName">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtFullName"
                                                    label="Full Name"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv form-group">
                                    <Field name="Company">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtCompany"
                                                    label="Company *"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv form-group">
                                    <Field name="Phone">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtPhone"
                                                    label="Phone *"
                                                    type="tel"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv form-group">
                                    <Field name="Email">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtEmail"
                                                    label="Email *"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv form-group">
                                    <Field name="Address1">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtAddress1"
                                                    label="Address 1 *"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv form-group">
                                    <Field name="Address2">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtAddress2"
                                                    label="Address 2"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv form-group">
                                    <Field name="City">
                                        {({ field, form }) => {
                                            return (
                                                <PVFormikTextBox
                                                    id="txtCity"
                                                    label="City *"
                                                    formObj={form}
                                                    fieldObj={field}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                                <div className="pv-form-row">
                                    <div className="pv-form-column">
                                        <div className="pv form-group">
                                            <Field name="State">
                                                {({ field, form }) => {
                                                    const { name } = field;
                                                    const { setFieldValue } = form;
                                                    return (
                                                        <div className="pv form-group">
                                                            <label
                                                                htmlFor="txtState"
                                                                className="control-label"
                                                            >
                                                                State *
                                                            </label>
                                                            <Select
                                                                id="txtState"
                                                                placeholder="Please Select"
                                                                name={name}
                                                                className={getInvalidFieldClass(
                                                                    field.name,
                                                                    form
                                                                )}
                                                                styles={pvSelectStyles}
                                                                components={{ DropdownIndicator }}
                                                                options={statesList}
                                                                getOptionLabel={(option) =>
                                                                    option.Name
                                                                }
                                                                getOptionValue={(option) =>
                                                                    option.Abbr
                                                                }
                                                                filterOptions={{
                                                                    ignoreCase: true,
                                                                    ignoreAccents: true,
                                                                    trim: true,
                                                                }}
                                                                defaultValue={form.values[name]}
                                                                onChange={(
                                                                    inputValue,
                                                                    { action }
                                                                ) => {
                                                                    if (action === 'clear') {
                                                                        setFieldValue(name, null);
                                                                    }
                                                                    if (
                                                                        inputValue &&
                                                                        inputValue.Abbr &&
                                                                        action === 'select-option'
                                                                    ) {
                                                                        setFieldValue(
                                                                            name,
                                                                            inputValue.Abbr
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                            <div
                                                                className={`pv-text-tiny ${
                                                                    getInvalidFieldClass(
                                                                        field.name,
                                                                        form
                                                                    ) === ''
                                                                        ? 'hidden'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {form.errors[name]}
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="pv-form-column">
                                        <div className="pv form-group">
                                            <Field name="ZipCode">
                                                {({ field, form }) => {
                                                    return (
                                                        <PVFormikTextBox
                                                            id="txtZipCode"
                                                            label="Zip Code *"
                                                            mask={zipMask}
                                                            formObj={form}
                                                            fieldObj={field}
                                                        />
                                                    );
                                                }}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                <span className="pv-text-small">* indicates required field</span>
                                <button
                                    style={{ cursor: 'pointer' }}
                                    type="submit"
                                    className={`pv btn ${getButtonStyle()} pull-right ${
                                        formSubmitting ? 'disabled' : ''
                                    }`}
                                >
                                    Submit
                                </button>
                            </Form>
                        </Formik>
                        <br className="clearfix" />
                        &nbsp;
                    </div>
                    <div className={formSuccess ? '' : 'hidden'}>
                        <div
                            className="pv-template1-header-form-header"
                            dangerouslySetInnerHTML={createHTMLText(formData.success)}
                        />
                    </div>
                    <div className={formError ? '' : 'hidden'}>
                        <div className="pv-template1-header-form-error">
                            <p>There was an error submitting your form.</p>
                            <p>Please reload the page and try again.</p>
                            <p>
                                If the problem persists, please contact PerfectVision at
                                800-205-8620.
                            </p>
                        </div>
                    </div>
                </div>
                <div
                    className="pv-template1-header-form-footer pv-text-small"
                    dangerouslySetInnerHTML={createHTMLText(formData.footer)}
                />
            </div>
        </React.Fragment>
    );
    /* eslint-enable react/no-danger */
};

PVTemplate1FormCatalog.propTypes = {
    formData: FormType,
};

PVTemplate1FormCatalog.defaultProps = {
    formData: {},
};

export default PVTemplate1FormCatalog;
